import { atom } from 'jotai';
import { allDatasetAtoms } from '../atoms';
import type { DatasetAreaSelection } from './types';
import { createdAreaRow, withoutSharedOnly } from './utils';

/** The Areas dataset's id, which a created area lands in. */
export const AREAS_DATASET_ID = 'areas';

/** Whether Map Query's Create New Area panel is open (Add > Area). */
export const createAreaOpenAtom = atom(false);

/**
 * Each dataset's checked rows, by dataset id. Held here rather than in the table
 * so a tab switch, which unmounts the results section, keeps them.
 */
export const areaSelectionsAtom = atom<Record<string, DatasetAreaSelection>>(
  {}
);

/**
 * Closes Create New Area and drops the checked rows with the rest of the form:
 * the checkboxes hide with the panel, so rows kept past a close would seed the
 * next opening without the user seeing them. A create closes the panel too.
 */
export const closeCreateAreaAtom = atom(null, (_get, set) => {
  set(createAreaOpenAtom, false);
  set(areaSelectionsAtom, {});
});

/**
 * Lists a created area in the Areas tab. The search runs again — without the
 * shared-only filter the tab starts on when the area isn't shared — and the
 * area, from its create response, leads that search's results until the index
 * returns it: areas are indexed in the background, so the search can miss it.
 */
export const listCreatedAreaAtom = atom(
  null,
  (get, set, area: { record: Record<string, unknown>; shared: boolean }) => {
    const { searchAtom, pinnedRowAtom } = allDatasetAtoms[AREAS_DATASET_ID];
    set(searchAtom, (current) => withoutSharedOnly(current, area.shared));
    // The search just started sets its payload before it fetches, so this is
    // the search the row is pinned under.
    const row = createdAreaRow(area.record);
    if (row) {
      set(pinnedRowAtom, { row, payload: get(searchAtom).currentPayload });
    }
  }
);
